import { useState } from "react";
import { CalendarBlank, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

/**
 * DateRangePicker
 * Single-input control with presets: Today, Yesterday, Last week, Last month, Last year, Custom.
 * Emits { from: ISOString | null, to: ISOString | null } on change.
 */
const PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last_week", label: "Last week" },
  { key: "last_month", label: "Last month" },
  { key: "last_year", label: "Last year" },
  { key: "custom", label: "Custom range" },
];

function preset(key) {
  const now = new Date();
  const start = new Date(now); start.setHours(0,0,0,0);
  const end = new Date(now); end.setHours(23,59,59,999);
  switch (key) {
    case "today":
      return { from: start, to: end };
    case "yesterday": {
      const s = new Date(start); s.setDate(s.getDate()-1);
      const e = new Date(end); e.setDate(e.getDate()-1);
      return { from: s, to: e };
    }
    case "last_week": {
      const s = new Date(start); s.setDate(s.getDate()-7);
      return { from: s, to: end };
    }
    case "last_month": {
      const s = new Date(start); s.setMonth(s.getMonth()-1);
      return { from: s, to: end };
    }
    case "last_year": {
      const s = new Date(start); s.setFullYear(s.getFullYear()-1);
      return { from: s, to: end };
    }
    default:
      return null;
  }
}

function formatShort(d) {
  if (!d) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function DateRangePicker({ value, onChange, testId = "date-range-picker" }) {
  const [active, setActive] = useState(value?.preset || null);
  const [customRange, setCustomRange] = useState(value?.range || { from: undefined, to: undefined });
  const [open, setOpen] = useState(false);

  const label = (() => {
    if (!active) return "All time";
    if (active !== "custom") {
      const p = PRESETS.find(p => p.key === active);
      return p ? p.label : "All time";
    }
    if (customRange?.from && customRange?.to) return `${formatShort(customRange.from)} → ${formatShort(customRange.to)}`;
    if (customRange?.from) return `${formatShort(customRange.from)} → …`;
    return "Custom range";
  })();

  const apply = (key) => {
    if (key === "custom") {
      setActive("custom");
      if (customRange?.from && customRange?.to) {
        onChange?.({
          from: customRange.from.toISOString(),
          to: new Date(customRange.to.getTime() + 86400000 - 1).toISOString(),
          preset: "custom",
          range: customRange,
        });
        setOpen(false);
      }
      return;
    }
    const p = preset(key);
    if (!p) return;
    setActive(key);
    onChange?.({
      from: p.from.toISOString(),
      to: p.to.toISOString(),
      preset: key,
    });
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    setActive(null);
    setCustomRange({ from: undefined, to: undefined });
    onChange?.(null);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={cn(
            "inline-flex items-center gap-2 h-9 px-3 rounded-md border bg-background text-sm min-w-[200px] justify-between transition-colors",
            "hover:bg-secondary/60"
          )}
        >
          <span className="flex items-center gap-2 truncate">
            <CalendarBlank size={14} className="text-muted-foreground" />
            <span className="truncate">{label}</span>
          </span>
          {active && (
            <span
              role="button"
              tabIndex={0}
              onClick={clear}
              onKeyDown={(e) => e.key === "Enter" && clear(e)}
              className="text-muted-foreground hover:text-foreground inline-flex"
              data-testid={`${testId}-clear`}
            >
              <X size={13} />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0" data-testid={`${testId}-popover`}>
        <div className="flex flex-col md:flex-row">
          <div className="border-b md:border-b-0 md:border-r p-2 flex flex-row md:flex-col gap-1 min-w-[160px] overflow-x-auto">
            {PRESETS.map(p => (
              <button
                key={p.key}
                data-testid={`${testId}-preset-${p.key}`}
                onClick={() => apply(p.key)}
                className={cn(
                  "text-left text-sm px-3 py-2 rounded-md whitespace-nowrap transition-colors",
                  active === p.key ? "bg-primary/10 text-primary font-medium" : "hover:bg-secondary/60 text-muted-foreground"
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          {active === "custom" && (
            <div className="p-3">
              <Calendar
                mode="range"
                selected={customRange}
                onSelect={(r) => {
                  setCustomRange(r || { from: undefined, to: undefined });
                  if (r?.from && r?.to) {
                    onChange?.({
                      from: r.from.toISOString(),
                      to: new Date(r.to.getTime() + 86400000 - 1).toISOString(),
                      preset: "custom",
                      range: r,
                    });
                  }
                }}
                numberOfMonths={2}
              />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
